import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Tabs,
  Tab,
} from '@mui/material';
import { RichTextEditor } from '../shared/RichTextEditor';
import { ModuleOutlineViewer } from './ModuleOutlineViewer';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AutoAwesome as AIIcon,
  Publish as PublishIcon,
  ArrowBack as BackIcon,
  Visibility as ViewIcon,
  Preview as PreviewIcon,
  Description as OutlineIcon,
  Quiz as PracticeIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getModule,
  getModuleContent,
  createUnit,
  generateContentAsync,
  pollJobUntilComplete,
  generateModuleOutlineAsync,
  publishModule,
  deleteUnit,
  updateUnit,
  updateUnitContent,
  GenerationJobStatus,
  Module,
  Unit,
  CreateUnitRequest,
  GenerateContentRequest,
  GenerateModuleOutlineRequest,
  ModuleOutline,
} from '../../services/lessonService';

export const ManageUnits: React.FC = () => {
  const { moduleId } = useParams<{ moduleId: string }>();
  const navigate = useNavigate();

  const [module, setModule] = useState<Module | null>(null);
  const [units, setUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [moduleOutline, setModuleOutline] = useState<ModuleOutline | null>(null);
  const [currentTab, setCurrentTab] = useState(0);

  // Dialog states
  const [addUnitDialogOpen, setAddUnitDialogOpen] = useState(false);
  const [generateDialogOpen, setGenerateDialogOpen] = useState(false);
  const [generateOutlineDialogOpen, setGenerateOutlineDialogOpen] = useState(false);
  const [viewContentDialogOpen, setViewContentDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUnitDialogOpen, setEditUnitDialogOpen] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState<Unit | null>(null);
  const [fullContent, setFullContent] = useState<any>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [generatingOutline, setGeneratingOutline] = useState(false);

  // Edit form states
  const [editIntroduction, setEditIntroduction] = useState('');
  const [editMainContent, setEditMainContent] = useState('');
  const [editSummary, setEditSummary] = useState('');

  // Form states
  const [unitTitle, setUnitTitle] = useState('');
  const [unitDescription, setUnitDescription] = useState('');
  const [unitDuration, setUnitDuration] = useState(45);
  const [unitObjectives, setUnitObjectives] = useState<string[]>(['']);
  const [additionalContext, setAdditionalContext] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationMessage, setGenerationMessage] = useState('');

  // Edit unit form states
  const [editUnitTitle, setEditUnitTitle] = useState('');
  const [editUnitDescription, setEditUnitDescription] = useState('');
  const [editUnitDuration, setEditUnitDuration] = useState(45);
  const [editUnitObjectives, setEditUnitObjectives] = useState<string[]>(['']);
  const [updatingUnit, setUpdatingUnit] = useState(false);

  // Outline generation form states
  const [outlineTopic, setOutlineTopic] = useState('');
  const [outlineDuration, setOutlineDuration] = useState('2 weeks');
  const [outlineStandards, setOutlineStandards] = useState('');

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const estimateMinutes = (estimatedTime: string | undefined, fallback = 45): number => {
    const match = estimatedTime?.match(/(\d+)/);
    return match ? Number(match[1]) : fallback;
  };

  const previousLessonsSummary = (unit: Unit): string => {
    const priorUnits = units
      .filter((candidate) => candidate.order_index < unit.order_index)
      .sort((a, b) => a.order_index - b.order_index);

    if (priorUnits.length === 0) {
      return 'This is the first lesson in the module.';
    }

    return priorUnits
      .map((candidate, index) => `${index + 1}. ${candidate.title}: ${candidate.description}`)
      .join('\n');
  };

  const moduleOverviewText = (): string => {
    if (moduleOutline?.module_overview.description) {
      return moduleOutline.module_overview.description;
    }
    if (!module) {
      return '';
    }
    return `${module.title} for grade ${module.grade_level} ${module.subject}`;
  };

  const lessonFocusText = (unit: Unit): string => {
    const baseFocus =
      unit.description || unit.learning_objectives.join(', ') || `Focus on ${unit.title}`;
    if (!additionalContext.trim()) {
      return baseFocus;
    }
    return `${baseFocus}\n\nTeacher additional instructions: ${additionalContext.trim()}`;
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    loadModule();
  }, [moduleId]);

  const loadModule = async () => {
    if (!moduleId) return;

    try {
      setLoading(true);
      const moduleData = await getModule(moduleId);
      setModule(moduleData);

      // Get module content to extract units and outline
      try {
        const content = await getModuleContent(moduleId);
        if (content.units) {
          setUnits(content.units);
        }
        // Load outline if it exists
        if (content.outline) {
          setModuleOutline(content.outline);
        }
      } catch (err) {
        // Module might not have content yet
        console.log('No content yet for module');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load module');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateOutline = async () => {
    if (!moduleId || !module) return;

    try {
      setGeneratingOutline(true);
      setGenerationProgress(0);
      setGenerationMessage('Starting outline generation...');

      const outlineData: GenerateModuleOutlineRequest = {
        subject: module.subject,
        grade_level: module.grade_level,
        topic: outlineTopic || module.title,
        duration: outlineDuration,
        state_standards: outlineStandards || undefined,
      };

      const jobResponse = await generateModuleOutlineAsync(moduleId, outlineData);
      const onProgress = (status: GenerationJobStatus) => {
        setGenerationProgress(status.progress_percentage);
        setGenerationMessage(status.progress_message || 'Processing...');
      };

      const finalStatus = await pollJobUntilComplete(jobResponse.job_id, onProgress);
      const response = finalStatus.result;
      if (!response?.outline) {
        throw new Error('Outline generation completed without an outline result.');
      }
      const createdUnits: Unit[] = [];

      if (units.length === 0 && response.outline.lessons?.length) {
        setGenerationMessage('Creating units from outline...');
        for (const lesson of response.outline.lessons) {
          const createdUnit = await createUnit(moduleId, {
            title: lesson.title,
            description: lesson.description,
            duration_minutes: estimateMinutes(lesson.estimated_time),
            learning_objectives: lesson.main_concepts,
          });
          createdUnits.push(createdUnit);
        }
      }

      setModuleOutline(response.outline);
      setGenerateOutlineDialogOpen(false);
      await loadModule();
      showSnackbar(
        createdUnits.length > 0
          ? `Module outline generated and ${createdUnits.length} units created.`
          : `Module outline generated with ${response.total_lessons} lessons.`,
      );
      setCurrentTab(createdUnits.length > 0 ? 1 : 0);
    } catch (err: any) {
      setError(err.message || 'Failed to generate outline');
    } finally {
      setGeneratingOutline(false);
      setGenerationProgress(0);
      setGenerationMessage('');
    }
  };

  const handleSaveOutline = async (updatedOutline: ModuleOutline) => {
    // TODO: Implement save outline to backend
    setModuleOutline(updatedOutline);
    showSnackbar('Outline updated successfully!');
  };

  const handleAddUnit = async () => {
    if (!moduleId) return;

    try {
      const unitData: CreateUnitRequest = {
        title: unitTitle,
        description: unitDescription,
        duration_minutes: unitDuration,
        learning_objectives: unitObjectives.filter((obj) => obj.trim() !== ''),
      };

      await createUnit(moduleId, unitData);
      setAddUnitDialogOpen(false);
      resetUnitForm();
      await loadModule();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create unit');
    }
  };

  const handleGenerateContent = async () => {
    if (!moduleId || !selectedUnit || !module) return;

    try {
      setGenerating(true);
      setGenerationProgress(0);
      setGenerationMessage('Starting content generation...');

      const generateData: GenerateContentRequest = {
        module_overview: moduleOverviewText(),
        module_objectives: module.learning_objectives,
        lesson_number: selectedUnit.order_index + 1,
        lesson_title: selectedUnit.title,
        lesson_focus: lessonFocusText(selectedUnit),
        previous_lessons_summary: previousLessonsSummary(selectedUnit),
        subject: module.subject,
        grade_level: module.grade_level,
        state_standards: module.standards?.join('\n') || undefined,
        duration_minutes: selectedUnit.duration_minutes || 45,
      };

      // Start async job
      const jobResponse = await generateContentAsync(moduleId, selectedUnit.unit_id, generateData);

      // Poll for completion with progress updates
      const onProgress = (status: GenerationJobStatus) => {
        setGenerationProgress(status.progress_percentage);
        setGenerationMessage(status.progress_message || 'Processing...');
      };

      await pollJobUntilComplete(jobResponse.job_id, onProgress);

      setGenerateDialogOpen(false);
      setAdditionalContext('');
      await loadModule();

      // Show success message
      setError(null);
      showSnackbar('Content generated successfully!');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to generate content');
    } finally {
      setGenerating(false);
      setGenerationProgress(0);
      setGenerationMessage('');
    }
  };

  const handleViewContent = async (unit: Unit) => {
    if (!moduleId) return;

    setSelectedUnit(unit);
    setViewContentDialogOpen(true);
    setLoadingContent(true);

    try {
      const contentMetadata = await getModuleContent(moduleId);
      const unitContent = contentMetadata.units.find((item: any) => item.unit_id === unit.unit_id);

      if (!unitContent?.content) {
        throw new Error('Generated content is not available for this unit yet.');
      }

      setFullContent(unitContent);
    } catch (err: any) {
      setError(err.message || 'Failed to load content');
      console.error('Error loading content:', err);
    } finally {
      setLoadingContent(false);
    }
  };

  const handleDeleteUnit = async () => {
    if (!moduleId || !selectedUnit) return;

    try {
      setDeleting(true);
      await deleteUnit(moduleId, selectedUnit.unit_id);
      setDeleteDialogOpen(false);
      setSelectedUnit(null);
      await loadModule(); // Reload to update the list
    } catch (err: any) {
      setError(err.message || 'Failed to delete unit');
    } finally {
      setDeleting(false);
    }
  };

  const handleEditContent = async (unit: Unit) => {
    if (!moduleId) return;

    setSelectedUnit(unit);
    setLoadingContent(true);

    try {
      const contentMetadata = await getModuleContent(moduleId);
      const unitContent = contentMetadata.units.find((item: any) => item.unit_id === unit.unit_id);

      if (!unitContent?.content) {
        throw new Error('Generated content is not available for this unit yet.');
      }

      // Populate edit fields with HTML content if available, otherwise use plain text
      setEditIntroduction(
        unitContent.content?.introduction?.html ||
          unitContent.content?.introduction?.plain_text ||
          '',
      );
      setEditMainContent(
        unitContent.content?.main_content?.html ||
          unitContent.content?.main_content?.plain_text ||
          '',
      );
      setEditSummary(
        unitContent.content?.summary?.html || unitContent.content?.summary?.plain_text || '',
      );
      setFullContent(unitContent);
      setEditDialogOpen(true);
    } catch (err: any) {
      setError(err.message || 'Failed to load content');
    } finally {
      setLoadingContent(false);
    }
  };

  const handleSaveContent = async () => {
    if (!moduleId || !selectedUnit) return;

    try {
      setSaving(true);

      // Update the content with HTML and plain text
      const updatedContent = {
        ...(fullContent?.content || {}),
        introduction: {
          html: editIntroduction,
          plain_text: editIntroduction.replace(/<[^>]*>/g, ''), // Strip HTML tags for plain text
          sections: [
            {
              title: 'Introduction',
              content: editIntroduction.replace(/<[^>]*>/g, ''),
            },
          ],
        },
        main_content: {
          html: editMainContent,
          plain_text: editMainContent.replace(/<[^>]*>/g, ''),
          sections: [
            {
              title: 'Main Content',
              content: editMainContent.replace(/<[^>]*>/g, ''),
            },
          ],
        },
        summary: {
          html: editSummary,
          plain_text: editSummary.replace(/<[^>]*>/g, ''),
          key_points: editSummary
            .replace(/<[^>]*>/g, '')
            .split('\n')
            .filter((line) => line.trim().startsWith('•'))
            .map((line) => line.trim().substring(1).trim()),
        },
        activities: fullContent?.content?.activities || [],
        required_materials: fullContent?.content?.required_materials || [],
      };

      await updateUnitContent(moduleId, selectedUnit.unit_id, updatedContent);
      setEditDialogOpen(false);
      await loadModule();
      showSnackbar('Content updated successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to save content');
    } finally {
      setSaving(false);
    }
  };

  const handlePublishModule = async () => {
    if (!moduleId) return;

    try {
      setPublishing(true);
      await publishModule(moduleId);
      setPublishDialogOpen(false);
      await loadModule();
      showSnackbar('Module published successfully! Students can now access this lesson.');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to publish module');
    } finally {
      setPublishing(false);
    }
  };

  const resetUnitForm = () => {
    setUnitTitle('');
    setUnitDescription('');
    setUnitDuration(45);
    setUnitObjectives(['']);
  };

  const handleEditUnit = (unit: Unit) => {
    setSelectedUnit(unit);
    setEditUnitTitle(unit.title);
    setEditUnitDescription(unit.description);
    setEditUnitDuration(unit.duration_minutes);
    setEditUnitObjectives(unit.learning_objectives.length > 0 ? unit.learning_objectives : ['']);
    setEditUnitDialogOpen(true);
  };

  const handleUpdateUnit = async () => {
    if (!moduleId || !selectedUnit) return;

    try {
      setUpdatingUnit(true);
      const updateData = {
        title: editUnitTitle,
        description: editUnitDescription,
        duration_minutes: editUnitDuration,
        learning_objectives: editUnitObjectives.filter((obj) => obj.trim() !== ''),
      };

      await updateUnit(moduleId, selectedUnit.unit_id, updateData);
      setEditUnitDialogOpen(false);
      await loadModule();
      showSnackbar('Unit updated successfully!');
    } catch (err: any) {
      setError(err.message || 'Failed to update unit');
    } finally {
      setUpdatingUnit(false);
    }
  };

  const addObjective = () => {
    setUnitObjectives([...unitObjectives, '']);
  };

  const updateObjective = (index: number, value: string) => {
    const newObjectives = [...unitObjectives];
    newObjectives[index] = value;
    setUnitObjectives(newObjectives);
  };

  const removeObjective = (index: number) => {
    setUnitObjectives(unitObjectives.filter((_, i) => i !== index));
  };

  const updateEditObjective = (index: number, value: string) => {
    const newObjectives = [...editUnitObjectives];
    newObjectives[index] = value;
    setEditUnitObjectives(newObjectives);
  };

  const removeEditObjective = (index: number) => {
    setEditUnitObjectives(editUnitObjectives.filter((_, i) => i !== index));
  };

  const addEditObjective = () => {
    setEditUnitObjectives([...editUnitObjectives, '']);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
      case 'outlined':
        return 'default';
      case 'generated':
        return 'primary';
      case 'reviewed':
        return 'secondary';
      case 'published':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!module) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Module not found</Alert>
      </Box>
    );
  }

  const canEditModule = module.status === 'draft' || module.status === 'published';
  const canChangeStructure = module.status === 'draft';

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate('/teacher/lessons')}
          sx={{ mb: 2 }}
        >
          Back to Lessons
        </Button>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              {module.title}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Chip label={module.subject} size="small" />
              <Chip label={`Grade ${module.grade_level}`} size="small" />
              <Chip
                label={module.status}
                color={getStatusColor(module.status) as any}
                size="small"
              />
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            {!moduleOutline && canChangeStructure && (
              <Button
                variant="contained"
                startIcon={<AIIcon />}
                onClick={() => setGenerateOutlineDialogOpen(true)}
                color="secondary"
              >
                Generate Outline with AI
              </Button>
            )}
            {module.status === 'published' && (
              <Button
                variant="contained"
                startIcon={<PreviewIcon />}
                onClick={() => navigate(`/teacher/lessons/${moduleId}/view`)}
                color="primary"
              >
                Preview Lesson
              </Button>
            )}
            {canChangeStructure && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => setAddUnitDialogOpen(true)}
              >
                Add Unit
              </Button>
            )}
            {canChangeStructure && units.length > 0 && (
              <Button
                variant="contained"
                startIcon={<PublishIcon />}
                onClick={() => setPublishDialogOpen(true)}
                color="success"
              >
                Publish Module
              </Button>
            )}
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Tabs for Outline and Units */}
      {moduleOutline && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
            <Tab icon={<OutlineIcon />} label="Module Outline" />
            <Tab label={`Units (${units.length})`} />
          </Tabs>
        </Box>
      )}

      {/* Module Outline Tab */}
      {moduleOutline && currentTab === 0 && (
        <ModuleOutlineViewer
          outline={moduleOutline}
          onSave={handleSaveOutline}
          editable={canEditModule}
        />
      )}

      {/* Units Tab */}
      {(!moduleOutline || currentTab === 1) && (
        <>
          {/* Module Info */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Learning Objectives
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                {module.learning_objectives.map((objective, index) => (
                  <li key={index}>
                    <Typography variant="body2">{objective}</Typography>
                  </li>
                ))}
              </Box>
            </CardContent>
          </Card>

          {/* Units List */}
          <Typography variant="h5" gutterBottom>
            Units ({units.length})
          </Typography>

          {units.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No units yet
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Start by adding units to your module
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setAddUnitDialogOpen(true)}
                >
                  Add First Unit
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {units.map((unit, index) => (
                <Card key={unit.unit_id}>
                  <CardContent>
                    <Box
                      sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="h6">
                            Unit {index + 1}: {unit.title}
                          </Typography>
                          <Chip
                            label={unit.status}
                            color={getStatusColor(unit.status) as any}
                            size="small"
                          />
                        </Box>

                        <Typography variant="body2" color="text.secondary" paragraph>
                          {unit.description}
                        </Typography>

                        <Typography variant="caption" color="text.secondary">
                          Duration: {unit.duration_minutes} minutes
                        </Typography>

                        {unit.learning_objectives.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Objectives:
                            </Typography>
                            <Box component="ul" sx={{ pl: 2, mt: 0 }}>
                              {unit.learning_objectives.map((obj, idx) => (
                                <li key={idx}>
                                  <Typography variant="body2">{obj}</Typography>
                                </li>
                              ))}
                            </Box>
                          </Box>
                        )}
                      </Box>

                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {(!unit.status ||
                          unit.status === 'draft' ||
                          unit.status === 'outlined') && (
                          <>
                            {canEditModule && (
                              <>
                                <Button
                                  variant="contained"
                                  startIcon={<AIIcon />}
                                  onClick={() => {
                                    setSelectedUnit(unit);
                                    setGenerateDialogOpen(true);
                                  }}
                                  color="primary"
                                >
                                  Generate Content
                                </Button>
                                <IconButton
                                  onClick={() => handleEditUnit(unit)}
                                  color="secondary"
                                  title="Edit Unit Details"
                                >
                                  <EditIcon />
                                </IconButton>
                              </>
                            )}
                            {canChangeStructure && (
                              <IconButton
                                color="error"
                                onClick={() => {
                                  setSelectedUnit(unit);
                                  setDeleteDialogOpen(true);
                                }}
                                title="Delete Unit"
                              >
                                <DeleteIcon />
                              </IconButton>
                            )}
                          </>
                        )}
                        {(unit.status === 'generated' ||
                          unit.status === 'reviewed' ||
                          unit.status === 'published') && (
                          <>
                            <Button
                              variant="contained"
                              startIcon={<ViewIcon />}
                              onClick={() => handleViewContent(unit)}
                              color="primary"
                            >
                              View
                            </Button>
                            <Button
                              variant="contained"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditContent(unit)}
                              color="secondary"
                              disabled={!canEditModule}
                            >
                              Edit Content
                            </Button>
                            {canEditModule && (
                              <IconButton
                                onClick={() => handleEditUnit(unit)}
                                color="secondary"
                                title="Edit Unit Details"
                              >
                                <EditIcon />
                              </IconButton>
                            )}
                            <Button
                              variant="outlined"
                              startIcon={<PracticeIcon />}
                              onClick={() =>
                                navigate(
                                  `/teacher/lessons/${moduleId}/units/${unit.unit_id}/edit?tab=practice`,
                                )
                              }
                              color="info"
                            >
                              Practice Questions
                            </Button>
                            {canEditModule && (
                              <Button
                                variant="outlined"
                                startIcon={<AIIcon />}
                                onClick={() => {
                                  setSelectedUnit(unit);
                                  setGenerateDialogOpen(true);
                                }}
                              >
                                Regenerate
                              </Button>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              ))}
            </Box>
          )}
        </>
      )}

      {/* Add Unit Dialog */}
      <Dialog
        open={addUnitDialogOpen}
        onClose={() => setAddUnitDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Add New Unit</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Unit Title"
              value={unitTitle}
              onChange={(e) => setUnitTitle(e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Description"
              value={unitDescription}
              onChange={(e) => setUnitDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />

            <TextField
              label="Duration (minutes)"
              type="number"
              value={unitDuration}
              onChange={(e) => setUnitDuration(Number(e.target.value))}
              fullWidth
              inputProps={{ min: 15, step: 15 }}
            />

            <Typography variant="subtitle2">Learning Objectives</Typography>
            {unitObjectives.map((objective, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label={`Objective ${index + 1}`}
                  value={objective}
                  onChange={(e) => updateObjective(index, e.target.value)}
                  fullWidth
                />
                {unitObjectives.length > 1 && (
                  <IconButton onClick={() => removeObjective(index)} color="error">
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={addObjective} variant="outlined">
              Add Objective
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddUnitDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleAddUnit}
            variant="contained"
            disabled={!unitTitle.trim() || unitObjectives.every((obj) => !obj.trim())}
          >
            Add Unit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Content Dialog */}
      <Dialog
        open={generateDialogOpen}
        onClose={() => !generating && setGenerateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {selectedUnit?.status === 'draft' || selectedUnit?.status === 'outlined'
            ? 'Generate Content'
            : 'Regenerate Content'}
        </DialogTitle>
        <DialogContent>
          {generating ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1" gutterBottom>
                Generating content with AI...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This may take 30-60 seconds
              </Typography>
              <LinearProgress sx={{ mt: 2 }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Alert severity="info">
                AI will generate comprehensive content including introduction, main content,
                activities, summary, and a quiz with 5 questions.
              </Alert>

              <TextField
                label="Additional Instructions (Optional)"
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                fullWidth
                multiline
                rows={3}
                placeholder="e.g., Include more visual examples, Focus on real-world applications"
              />

              <Typography variant="caption" color="text.secondary">
                Estimated cost: $0.10 - $0.30
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateDialogOpen(false)} disabled={generating}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerateContent}
            variant="contained"
            disabled={generating}
            startIcon={<AIIcon />}
          >
            {selectedUnit?.status === 'draft' || selectedUnit?.status === 'outlined'
              ? 'Generate'
              : 'Regenerate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Content Dialog */}
      <Dialog
        open={viewContentDialogOpen}
        onClose={() => setViewContentDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>{selectedUnit?.title} - Generated Content</DialogTitle>
        <DialogContent dividers>
          {loadingContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : fullContent ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {/* Introduction */}
              {fullContent.content?.introduction && (
                <Box>
                  <Typography variant="h6" gutterBottom color="primary">
                    Introduction
                  </Typography>
                  <RichTextEditor
                    content={
                      fullContent.content.introduction.html ||
                      fullContent.content.introduction.plain_text ||
                      ''
                    }
                    onChange={() => undefined}
                    readOnly={true}
                    minHeight={100}
                  />
                  <Divider sx={{ mt: 2 }} />
                </Box>
              )}

              {/* Module Vocabulary */}
              {fullContent.content?.module_vocabulary &&
                fullContent.content.module_vocabulary.length > 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom color="primary">
                      Key Vocabulary (Module)
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {fullContent.content.module_vocabulary.map((term: string, idx: number) => (
                        <Chip key={idx} label={term} variant="outlined" />
                      ))}
                    </Box>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                )}

              {/* Lesson Vocabulary */}
              {fullContent.content?.lesson_vocabulary &&
                fullContent.content.lesson_vocabulary.length > 0 && (
                  <Box>
                    <Typography variant="h6" gutterBottom color="primary">
                      Lesson Vocabulary
                    </Typography>
                    <List>
                      {fullContent.content.lesson_vocabulary.map((vocab: any, idx: number) => (
                        <ListItem key={idx}>
                          <ListItemText
                            primary={<strong>{vocab.term}</strong>}
                            secondary={vocab.definition}
                          />
                        </ListItem>
                      ))}
                    </List>
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                )}

              {/* Main Content */}
              {fullContent.content?.main_content && (
                <Box>
                  <Typography variant="h6" gutterBottom color="primary">
                    Main Content
                  </Typography>
                  <RichTextEditor
                    content={
                      fullContent.content.main_content.html ||
                      fullContent.content.main_content.plain_text ||
                      ''
                    }
                    onChange={() => undefined}
                    readOnly={true}
                    minHeight={200}
                  />
                  <Divider sx={{ mt: 2 }} />
                </Box>
              )}

              {/* Activities */}
              {fullContent.content?.activities && fullContent.content.activities.length > 0 && (
                <Box>
                  <Typography variant="h6" gutterBottom color="primary">
                    Activities
                  </Typography>
                  {fullContent.content.activities.map((activity: any, idx: number) => (
                    <Card key={idx} sx={{ mb: 2 }}>
                      <CardContent>
                        <Typography variant="subtitle1" gutterBottom>
                          {activity.title}
                        </Typography>
                        <Chip label={activity.type} size="small" sx={{ mb: 1 }} />
                        <Typography variant="body2" paragraph>
                          {activity.description}
                        </Typography>
                        {activity.instructions && activity.instructions.length > 0 && (
                          <>
                            <Typography variant="subtitle2" gutterBottom>
                              Instructions:
                            </Typography>
                            <List dense>
                              {activity.instructions.map((instruction: string, i: number) => (
                                <ListItem key={i}>
                                  <ListItemText primary={`${i + 1}. ${instruction}`} />
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}
                        {activity.materials && activity.materials.length > 0 && (
                          <>
                            <Typography variant="subtitle2" gutterBottom>
                              Materials:
                            </Typography>
                            <Typography variant="body2">{activity.materials.join(', ')}</Typography>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                  <Divider />
                </Box>
              )}

              {/* Summary */}
              {fullContent.content?.summary && (
                <Box>
                  <Typography variant="h6" gutterBottom color="primary">
                    Summary
                  </Typography>
                  <RichTextEditor
                    content={
                      fullContent.content.summary.html ||
                      fullContent.content.summary.plain_text ||
                      ''
                    }
                    onChange={() => undefined}
                    readOnly={true}
                    minHeight={100}
                  />
                  <Divider sx={{ mt: 2 }} />
                </Box>
              )}

              {/* Teacher Supports */}
              {fullContent.content &&
                (fullContent.content.teacher_notes?.length > 0 ||
                  fullContent.content.vocabulary?.length > 0 ||
                  fullContent.content.checks_for_understanding?.length > 0 ||
                  fullContent.content.common_misconceptions?.length > 0 ||
                  fullContent.content.differentiation ||
                  fullContent.content.assessment) && (
                  <Box>
                    <Typography variant="h6" gutterBottom color="primary">
                      Teacher Supports
                    </Typography>
                    {fullContent.content.teacher_notes?.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Notes
                        </Typography>
                        <List dense>
                          {fullContent.content.teacher_notes.map((note: string, idx: number) => (
                            <ListItem key={idx}>
                              <ListItemText primary={note} />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                    {fullContent.content.vocabulary?.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Vocabulary
                        </Typography>
                        <List dense>
                          {fullContent.content.vocabulary.map((item: any, idx: number) => (
                            <ListItem key={idx}>
                              <ListItemText
                                primary={item.term}
                                secondary={
                                  item.student_friendly_explanation || item.definition || ''
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Box>
                    )}
                    {fullContent.content.checks_for_understanding?.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Checks for Understanding
                        </Typography>
                        <List dense>
                          {fullContent.content.checks_for_understanding.map(
                            (check: string, idx: number) => (
                              <ListItem key={idx}>
                                <ListItemText primary={check} />
                              </ListItem>
                            ),
                          )}
                        </List>
                      </Box>
                    )}
                    {fullContent.content.common_misconceptions?.length > 0 && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Common Misconceptions
                        </Typography>
                        <List dense>
                          {fullContent.content.common_misconceptions.map(
                            (misconception: string, idx: number) => (
                              <ListItem key={idx}>
                                <ListItemText primary={misconception} />
                              </ListItem>
                            ),
                          )}
                        </List>
                      </Box>
                    )}
                    {fullContent.content.differentiation && (
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Differentiation
                        </Typography>
                        {[
                          [
                            'Struggling Learners',
                            fullContent.content.differentiation.struggling_learners,
                          ],
                          [
                            'English Language Learners',
                            fullContent.content.differentiation.english_language_learners,
                          ],
                          [
                            'Advanced Learners',
                            fullContent.content.differentiation.advanced_learners,
                          ],
                        ].map(([label, items]: any) =>
                          items?.length ? (
                            <Box key={label} sx={{ mb: 1 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {label}
                              </Typography>
                              <Typography variant="body2">{items.join('; ')}</Typography>
                            </Box>
                          ) : null,
                        )}
                      </Box>
                    )}
                    {fullContent.content.assessment && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Assessment
                        </Typography>
                        {fullContent.content.assessment.formative_checks?.length > 0 && (
                          <Typography variant="body2" paragraph>
                            <strong>Formative checks:</strong>{' '}
                            {fullContent.content.assessment.formative_checks.join('; ')}
                          </Typography>
                        )}
                        {fullContent.content.assessment.exit_ticket && (
                          <Typography variant="body2" paragraph>
                            <strong>Exit ticket:</strong>{' '}
                            {fullContent.content.assessment.exit_ticket}
                          </Typography>
                        )}
                        {fullContent.content.assessment.success_criteria?.length > 0 && (
                          <Typography variant="body2">
                            <strong>Success criteria:</strong>{' '}
                            {fullContent.content.assessment.success_criteria.join('; ')}
                          </Typography>
                        )}
                      </Box>
                    )}
                    <Divider sx={{ mt: 2 }} />
                  </Box>
                )}

              {/* Quiz */}
              {fullContent.test && (
                <Box>
                  <Typography variant="h6" gutterBottom color="primary">
                    Unit Test ({fullContent.test.questions.length} questions)
                  </Typography>
                  {fullContent.test.questions.map((question: any, idx: number) => (
                    <Card key={idx} sx={{ mb: 2 }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                          <Chip label={question.difficulty} size="small" />
                          <Chip label={`${question.points} pts`} size="small" color="primary" />
                        </Box>
                        <Typography variant="subtitle1" gutterBottom>
                          {idx + 1}. {question.question_text}
                        </Typography>
                        {question.options && (
                          <List dense>
                            {question.options.map((option: any, i: number) => (
                              <ListItem key={i}>
                                <ListItemText
                                  primary={option.text}
                                  sx={{
                                    color: option.is_correct ? 'success.main' : 'text.primary',
                                    fontWeight: option.is_correct ? 'bold' : 'normal',
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        )}
                        <Alert severity="info" sx={{ mt: 1 }}>
                          <Typography variant="body2">
                            <strong>Explanation:</strong> {question.explanation}
                          </Typography>
                        </Alert>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Box>
          ) : (
            <Alert severity="info">No content available</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewContentDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Unit Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deleting && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Unit?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Are you sure you want to delete "{selectedUnit?.title}"? This action cannot be undone.
            All content and quiz questions will be permanently deleted.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button onClick={handleDeleteUnit} color="error" disabled={deleting}>
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Content Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => !saving && setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>Edit Content - {selectedUnit?.title}</DialogTitle>
        <DialogContent dividers>
          {loadingContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Alert severity="info">
                Use the rich text editor below to format your content. You can add headings, lists,
                images, and even LaTeX equations.
              </Alert>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Introduction
                </Typography>
                <RichTextEditor
                  content={editIntroduction}
                  onChange={(html) => setEditIntroduction(html)}
                  placeholder="Enter introduction text..."
                  minHeight={150}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Main Content
                </Typography>
                <RichTextEditor
                  content={editMainContent}
                  onChange={(html) => setEditMainContent(html)}
                  placeholder="Enter main content text..."
                  minHeight={300}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Summary
                </Typography>
                <RichTextEditor
                  content={editSummary}
                  onChange={(html) => setEditSummary(html)}
                  placeholder="Enter summary text..."
                  minHeight={150}
                />
              </Box>

              <Typography variant="caption" color="text.secondary">
                Note: Activities and quiz questions are preserved. You can edit them in a future
                update.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSaveContent} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish Confirmation Dialog */}
      <Dialog open={publishDialogOpen} onClose={() => !publishing && setPublishDialogOpen(false)}>
        <DialogTitle>Publish Module?</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Are you sure you want to publish this module? Students will be able to access it.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishDialogOpen(false)} disabled={publishing}>
            Cancel
          </Button>
          <Button
            onClick={handlePublishModule}
            color="success"
            variant="contained"
            disabled={publishing}
          >
            {publishing ? 'Publishing...' : 'Publish'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Generate Outline Dialog */}
      <Dialog
        open={generateOutlineDialogOpen}
        onClose={() => !generatingOutline && setGenerateOutlineDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Generate Module Outline with AI</DialogTitle>
        <DialogContent>
          {generatingOutline ? (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body1" gutterBottom>
                Generating comprehensive module outline...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This may take 30-60 seconds
              </Typography>
              <LinearProgress sx={{ mt: 2 }} />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <Alert severity="info">
                AI will generate a comprehensive module outline including learning objectives,
                vocabulary, lesson structure, assessments, and differentiation strategies. Units
                will be automatically created from the lesson outline.
              </Alert>

              <TextField
                label="Topic/Theme"
                value={outlineTopic}
                onChange={(e) => setOutlineTopic(e.target.value)}
                fullWidth
                placeholder={module.title}
                helperText="Leave blank to use module title"
              />

              <TextField
                label="Duration"
                value={outlineDuration}
                onChange={(e) => setOutlineDuration(e.target.value)}
                fullWidth
                placeholder="e.g., 2 weeks, 1 month"
              />

              <TextField
                label="State Standards (Optional)"
                value={outlineStandards}
                onChange={(e) => setOutlineStandards(e.target.value)}
                fullWidth
                multiline
                rows={2}
                placeholder="e.g., CCSS.MATH.CONTENT.5.NF.A.1"
              />

              <Typography variant="caption" color="text.secondary">
                Estimated cost: $0.15 - $0.40
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOutlineDialogOpen(false)} disabled={generatingOutline}>
            Cancel
          </Button>
          <Button
            onClick={handleGenerateOutline}
            variant="contained"
            disabled={generatingOutline}
            startIcon={<AIIcon />}
          >
            Generate Outline
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Unit Dialog */}
      <Dialog
        open={editUnitDialogOpen}
        onClose={() => !updatingUnit && setEditUnitDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Unit</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Unit Title"
              value={editUnitTitle}
              onChange={(e) => setEditUnitTitle(e.target.value)}
              fullWidth
              required
            />

            <TextField
              label="Description"
              value={editUnitDescription}
              onChange={(e) => setEditUnitDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />

            <TextField
              label="Duration (minutes)"
              type="number"
              value={editUnitDuration}
              onChange={(e) => setEditUnitDuration(Number(e.target.value))}
              fullWidth
              inputProps={{ min: 15, step: 15 }}
            />

            <Typography variant="subtitle2">Learning Objectives</Typography>
            {editUnitObjectives.map((objective, index) => (
              <Box key={index} sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  label={`Objective ${index + 1}`}
                  value={objective}
                  onChange={(e) => updateEditObjective(index, e.target.value)}
                  fullWidth
                />
                {editUnitObjectives.length > 1 && (
                  <IconButton onClick={() => removeEditObjective(index)} color="error">
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>
            ))}
            <Button startIcon={<AddIcon />} onClick={addEditObjective} variant="outlined">
              Add Objective
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditUnitDialogOpen(false)} disabled={updatingUnit}>
            Cancel
          </Button>
          <Button
            onClick={handleUpdateUnit}
            variant="contained"
            disabled={
              !editUnitTitle.trim() ||
              editUnitObjectives.every((obj) => !obj.trim()) ||
              updatingUnit
            }
          >
            {updatingUnit ? 'Updating...' : 'Update Unit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};
